import { isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectorRef,
  OnChanges,
  OnInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  TemplateRef,
  Inject,
  PLATFORM_ID
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { AppActionTypes } from "@app/store/actions/app.actions";
import { LoadContentType } from "@app/store/actions/content-type.actions";
import {
  LoadNestedCommentsSuccess,
  CreateNestedComment,
  LoadNestedComments
} from "@app/store/actions/nested-comments.actions";
import { selectContentType } from "@app/store/selectors/app/content-type.selectors";
import { selectNestedCommentsByContentTypeIdAndObjectId } from "@app/store/selectors/app/nested-comments.selectors";
import { ContentTypeInterface } from "@core/interfaces/content-type.interface";
import { NestedCommentInterface } from "@core/interfaces/nested-comment.interface";
import { UserInterface } from "@core/interfaces/user.interface";
import { DeviceService } from "@core/services/device.service";
import { LoadingService } from "@core/services/loading.service";
import { RouterService } from "@core/services/router.service";
import { distinctUntilChangedObj, UtilsService } from "@core/services/utils/utils.service";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { Actions, ofType } from "@ngrx/effects";
import { Store, select } from "@ngrx/store";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { fadeInOut } from "@shared/animations";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { ScrollVisibilityDirective } from "@shared/directives/scroll-visibility.directive";
import { Observable } from "rxjs";
import { filter, map, take, takeUntil, tap } from "rxjs/operators";

export enum NestedCommentsAutoStartTopLevelStrategy {
  ALWAYS = "ALWAYS",
  IF_NO_COMMENTS = "IF_NO_COMMENTS"
}

export type NestedCommentsTopLevelFormPlacement = "TOP" | "BOTTOM";

@Component({
  selector: "astrobin-nested-comments",
  templateUrl: "./nested-comments.component.html",
  styleUrls: ["./nested-comments.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOut]
})
export class NestedCommentsComponent extends BaseComponentDirective implements OnInit, OnChanges {
  @Input()
  contentType: ContentTypeInterface;

  @Input()
  objectId: number;

  @Input()
  highlightId: number;

  @Input()
  title: string = this.translateService.instant("Comments");

  @Input()
  info: string;

  @Input()
  infoClass: string;

  @Input()
  addCommentLabel: string = this.translateService.instant("Add a comment");

  @Input()
  noCommentsLabel: string = this.translateService.instant("There are no comments yet.");

  @Input()
  showCloseButton = false;

  @Input()
  showReplyButton = true;

  @Input()
  allowSelfReply = true;

  @Input()
  allowModeration = false;

  @Input()
  showTopLevelButton = true;

  @Input()
  restrictReplyToUserId: UserInterface["id"];

  @Input()
  autoStartTopLevelStrategy: NestedCommentsAutoStartTopLevelStrategy = null;

  @Input()
  topLevelFormPlacement: NestedCommentsTopLevelFormPlacement = "TOP";

  @Input()
  category?: string;

  @Input()
  topLevelFormHeight: number;

  @Input()
  useOffcanvasOnTouchDevices = false;

  @Output()
  close = new EventEmitter<void>();

  @Output()
  formDirtyChange = new EventEmitter<boolean>();

  comments$: Observable<NestedCommentInterface[]>;
  loadingComments = true;
  model: {
    topLevelComment: string; // JSON from TipTap
    topLevelCommentHtml?: string; // HTML from TipTap
    topLevelCommentText?: string; // Plain text from TipTap
  } = { topLevelComment: "" };
  form = new FormGroup({});
  fields: FormlyFieldConfig[];
  offcanvasFields: FormlyFieldConfig[];
  showTopLevelForm = false;
  _lastFetchedComments: NestedCommentInterface[] = null;
  _autoStartTopLevelRetries = 0;
  hasNestedDirtyForm = false;

  // This the comment type of NestedComment, used to like comments.
  protected commentContentType: ContentTypeInterface;
  protected forceVisible = false;
  protected isInViewport = false;
  protected _isBrowser = false;
  protected _currentOffcanvasRef: any = null;
  protected _isSubmittingTopLevelComment = false;

  @ViewChild(ScrollVisibilityDirective)
  private _scrollVisibilityDirective: ScrollVisibilityDirective;

  @ViewChild("topLevelCommentOffcanvasTpl", { static: true })
  private _topLevelCommentOffcanvasTemplate: TemplateRef<any>;

  constructor(
    public readonly store$: Store,
    public readonly actions$: Actions,
    public readonly loadingService: LoadingService,
    public readonly translateService: TranslateService,
    public readonly routerService: RouterService,
    public readonly utilsService: UtilsService,
    public readonly changeDetectorRef: ChangeDetectorRef,
    public readonly activatedRoute: ActivatedRoute,
    public readonly deviceService: DeviceService,
    public readonly offcanvas: NgbOffcanvas,
    @Inject(PLATFORM_ID) private readonly _platformId: any
  ) {
    super(store$);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this._isBrowser = isPlatformBrowser(this._platformId);
    const fragment = this.activatedRoute.snapshot.fragment;

    // Support both #c123 and #imageId-c123 fragment formats
    this.forceVisible = !!fragment && (!!fragment.match(/^c\d+$/) || !!fragment.match(/^\d+-c\d+$/));

    this._initCommentContentType();
    this._setupFormListeners();
  }

  ngOnChanges() {
    // Reset the scroll visibility directive if it's been initialized
    if (this._scrollVisibilityDirective) {
      this._scrollVisibilityDirective.reset();
      // Also reset our internal visibility tracking to ensure refresh happens
      this.isInViewport = false;
    }

    this._initComments();
    this._initFields();
    this.offcanvasFields = this._getOffcanvasFields();
  }

  onVisibilityChange(isIntersecting: boolean): void {
    if (isIntersecting) {
      const wasInViewport = this.isInViewport;
      this.isInViewport = true;

      // Always refresh if this is the first time becoming visible
      // or if the directive was reset due to input changes
      if (!wasInViewport) {
        this.refresh();
      }
    } else {
      this.isInViewport = false;
    }
  }

  isDirty(): boolean {
    // Check if top-level form is dirty and has content
    const topLevelValue = this.form.get("topLevelComment")?.value;

    const hasTopLevelContent =
      this.showTopLevelForm &&
      this.form.dirty &&
      !!topLevelValue &&
      typeof topLevelValue === "string" &&
      !!(topLevelValue as string).trim() &&
      topLevelValue !== '{"type":"doc","content":[{"type":"paragraph"}]}';

    if (hasTopLevelContent) {
      return true;
    }

    // Check if any nested comment form is dirty
    if (this.hasNestedDirtyForm) {
      return true;
    }

    return false;
  }

  onNestedCommentFormDirtyChange(isDirty: boolean): void {
    this.hasNestedDirtyForm = isDirty;
    this.formDirtyChange.emit(this.isDirty());
  }

  refresh() {
    this.actions$
      .pipe(
        ofType(AppActionTypes.LOAD_NESTED_COMMENTS_SUCCESS),
        filter(
          (action: LoadNestedCommentsSuccess) =>
            action.payload.contentTypeId === this.contentType.id &&
            action.payload.objectId === this.objectId &&
            action.payload.category === this.category
        ),
        take(1)
      )
      .subscribe(() => {
        this.loadingComments = false;
        this.changeDetectorRef.markForCheck();
      });

    this.loadingComments = true;
    this.store$.dispatch(
      new LoadNestedComments({
        contentTypeId: this.contentType.id,
        objectId: this.objectId,
        category: this.category
      })
    );
  }

  cancelTopLevelComment() {
    this.form.reset();
    this.showTopLevelForm = false;
  }

  submitTopLevelComment() {
    this.currentUser$.pipe(take(1)).subscribe(user => {
      if (!user) {
        void this.routerService.redirectToLogin();
        return;
      }

      const textProsemirror = this.form.get("topLevelComment").value;
      const textHtml = this.model.topLevelCommentHtml || "";
      const text = this.model.topLevelCommentText || "";

      this.store$.dispatch(
        new CreateNestedComment({
          nestedComment: {
            contentType: this.contentType.id,
            objectId: this.objectId,
            textProsemirror: textProsemirror,
            textHtml: textHtml,
            text: text,
            html: textHtml, // Keep for backward compatibility
            parent: null,
            category: this.category
          }
        })
      );

      this.actions$
        .pipe(
          ofType(AppActionTypes.CREATE_NESTED_COMMENT_SUCCESS),
          take(1),
          tap(() => this.cancelTopLevelComment())
        )
        .subscribe();
    });
  }

  onAddCommentClicked(event: Event) {
    if (event) {
      event.preventDefault();
    }

    this.currentUser$.pipe(take(1)).subscribe(user => {
      if (!user) {
        void this.routerService.redirectToLogin();
        return;
      }

      if (
        this._isBrowser &&
        this.useOffcanvasOnTouchDevices &&
        this.deviceService.isTouchEnabled() &&
        this.deviceService.xsMax()
      ) {
        this._openTopLevelCommentOffcanvas();
      } else {
        this.showTopLevelForm = true;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  protected commentTrackByFn(index: number, comment: NestedCommentInterface) {
    return comment.id;
  }

  submitTopLevelCommentOffcanvas(): void {
    if (this._isSubmittingTopLevelComment) {
      return;
    }

    this._isSubmittingTopLevelComment = true;

    // Disable the form control (which will disable the TipTap editor)
    this.form.get("topLevelComment")?.disable();

    this.changeDetectorRef.markForCheck();

    this.currentUser$.pipe(take(1)).subscribe(user => {
      if (!user) {
        this._isSubmittingTopLevelComment = false;

        // Re-enable the form control
        this.form.get("topLevelComment")?.enable();

        this.changeDetectorRef.markForCheck();
        void this.routerService.redirectToLogin();
        return;
      }

      const textProsemirror = this.form.get("topLevelComment").value;
      const textHtml = this.model.topLevelCommentHtml || "";
      const text = this.model.topLevelCommentText || "";

      this.store$.dispatch(
        new CreateNestedComment({
          nestedComment: {
            contentType: this.contentType.id,
            objectId: this.objectId,
            textProsemirror: textProsemirror,
            textHtml: textHtml,
            text: text,
            html: textHtml,
            parent: null,
            category: this.category
          }
        })
      );

      this.actions$
        .pipe(
          ofType(AppActionTypes.CREATE_NESTED_COMMENT_SUCCESS, AppActionTypes.CREATE_NESTED_COMMENT_FAILURE),
          take(1)
        )
        .subscribe((action: any) => {
          this._isSubmittingTopLevelComment = false;

          // Re-enable the form control
          this.form.get("topLevelComment")?.enable();

          this.changeDetectorRef.markForCheck();

          if (action.type === AppActionTypes.CREATE_NESTED_COMMENT_SUCCESS) {
            this.form.reset();
            if (this._currentOffcanvasRef) {
              this._currentOffcanvasRef.close();
            }
          }
        });
    });
  }

  private _openTopLevelCommentOffcanvas(): void {
    const offcanvasRef = this.offcanvas.open(this._getTopLevelCommentOffcanvasTemplate(), {
      position: "bottom",
      backdrop: "static",
      keyboard: false,
      scroll: false,
      panelClass: "image-viewer-offcanvas",
      backdropClass: "image-viewer-offcanvas-backdrop"
    });

    // Store the reference for later use
    this._currentOffcanvasRef = offcanvasRef;

    offcanvasRef.result
      .then(() => {
        this._currentOffcanvasRef = null;
      })
      .catch(() => {
        this._currentOffcanvasRef = null;
      });

    offcanvasRef.shown.subscribe(() => {
      this._focusTipTapEditor();
    });
  }

  private _focusTipTapEditor(): void {
    if (!this._isBrowser) {
      return;
    }

    // Try to focus immediately to preserve user gesture context for mobile
    const tryFocus = () => {
      const editor = document.querySelector(".offcanvas .tiptap-content .ProseMirror");
      if (editor && editor instanceof HTMLElement) {
        editor.focus();
        return true;
      }
      return false;
    };

    // First attempt - synchronous
    if (tryFocus()) {
      return;
    }

    // If not found immediately, try a few more times quickly
    // but keep it within the gesture context timeframe
    let attempts = 0;
    const quickRetry = () => {
      if (attempts >= 3 || tryFocus()) {
        return;
      }
      attempts++;
      this.utilsService.delay(10).subscribe(quickRetry);
    };

    quickRetry();
  }

  confirmCloseOffcanvas(dismiss: () => void): void {
    // Check if there's content in the form
    const hasContent = this._hasTopLevelCommentContent();

    if (hasContent && !this._isSubmittingTopLevelComment) {
      const confirmed = confirm(
        this.translateService.instant("You have unsaved changes. Are you sure you want to close?")
      );
      if (confirmed) {
        this.form.reset();
        dismiss();
      }
    } else {
      this.form.reset();
      dismiss();
    }
  }

  private _hasTopLevelCommentContent(): boolean {
    const topLevelValue = this.form.get("topLevelComment")?.value;

    return !!(
      topLevelValue &&
      typeof topLevelValue === "string" &&
      (topLevelValue as string).trim() &&
      topLevelValue !== '{"type":"doc","content":[{"type":"paragraph"}]}'
    );
  }

  private _getTopLevelCommentOffcanvasTemplate() {
    return this._topLevelCommentOffcanvasTemplate;
  }

  private _initComments() {
    this.comments$ = this.store$
      .select(selectNestedCommentsByContentTypeIdAndObjectId(this.contentType.id, this.objectId, this.category))
      .pipe(
        filter(comments => comments !== null),
        distinctUntilChangedObj(),
        map(comments => UtilsService.sortParent(comments) as NestedCommentInterface[]),
        tap(() => this.loadingService.setLoading(false)),
        tap(comments => {
          this._lastFetchedComments = comments;

          this.utilsService.delay(200).subscribe(() => {
            this._autoStartTopLevel();
            this.changeDetectorRef.markForCheck();
          });
        }),
        takeUntil(this.destroyed$)
      );
  }

  private _autoStartTopLevel() {
    if (this._autoStartTopLevelRetries >= 10) {
      return;
    }

    this._autoStartTopLevelRetries++;

    if (this.loadingComments) {
      this.utilsService.delay(200).subscribe(() => {
        this._autoStartTopLevel();
        this.changeDetectorRef.markForCheck();
      });
      return;
    }

    const comments = this._lastFetchedComments;

    if (
      (!this.showTopLevelForm && this.autoStartTopLevelStrategy === NestedCommentsAutoStartTopLevelStrategy.ALWAYS) ||
      (this.autoStartTopLevelStrategy === NestedCommentsAutoStartTopLevelStrategy.IF_NO_COMMENTS &&
        comments &&
        comments.length === 0)
    ) {
      this.onAddCommentClicked(null);
    }
  }

  private _initFields() {
    this.fields = [
      {
        key: "topLevelComment",
        type: "tiptap",
        wrappers: ["default-wrapper"],
        id: `top-level-comment-field-${this.contentType.id}-${this.objectId}`,
        props: {
          required: true,
          autofocus: true,
          height: `${this.topLevelFormHeight || 300}px`,
          placeholder: this.translateService.instant("Write your comment..."),
          onContentChange: ({ json, html, text }) => {
            // Store all formats for backend submission
            this.model.topLevelCommentHtml = html;
            this.model.topLevelCommentText = text;
          }
        }
      }
    ];
  }

  private _getOffcanvasFields(): FormlyFieldConfig[] {
    return [
      {
        key: "topLevelComment",
        type: "tiptap",
        wrappers: ["default-wrapper"],
        id: `top-level-comment-field-offcanvas-${this.contentType.id}-${this.objectId}`,
        props: {
          required: true,
          height: "200px",
          placeholder: this.translateService.instant("Write your comment..."),
          onContentChange: ({ json, html, text }) => {
            // Store all formats for backend submission
            this.model.topLevelCommentHtml = html;
            this.model.topLevelCommentText = text;
          }
        }
      }
    ];
  }

  private _initCommentContentType() {
    const payload = { appLabel: "nested_comments", model: "nestedcomment" };
    this.store$
      .pipe(
        select(selectContentType, payload),
        filter(contentType => !!contentType),
        take(1)
      )
      .subscribe(contentType => {
        this.commentContentType = contentType;
        this.changeDetectorRef.markForCheck();
      });
    this.store$.dispatch(new LoadContentType(payload));
  }

  private _setupFormListeners(): void {
    // Listen to changes in the top-level form
    this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.formDirtyChange.emit(this.isDirty());
    });
  }
}
